import { motion } from "framer-motion";
import { ArrowUpRight, Wifi, Flame, Users } from "lucide-react";
import { ROOMS, WA_URL } from "./data";
import SectionHeader from "./SectionHeader";

const featureIcon = (label) => {
  if (label.toLowerCase().includes("wi")) return Wifi;
  if (label.toLowerCase().includes("hot")) return Flame;
  return Users;
};

export default function Rooms() {
  return (
    <section
      id="rooms"
      data-testid="rooms-section"
      className="relative bg-[color:var(--paper-2)] text-[color:var(--ink)]"
    >
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-16 sm:py-20 lg:py-24">
        <SectionHeader
          eyebrow="Section 02 · Rooms & Stay"
          title={
            <>
              Three ways to <span className="font-display-italic text-[color:var(--clay)]">stay</span>.
              One estate. Every one of them faces the fog.
            </>
          }
          intro="Every room has hot water, free Wi-Fi, and a door that opens onto something worth walking to."
        />

        <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {ROOMS.map((r, i) => (
            <motion.article
              key={r.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: i * 0.1, duration: 0.9, ease: [0.2, 0.9, 0.2, 1] }}
              className="room-card group relative overflow-hidden rounded-[24px] bg-[color:var(--paper)] ring-1 ring-[color:var(--line)]"
              data-testid={`room-card-${r.id}`}
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-[color:var(--forest)]">
                <img
                  src={r.img}
                  alt={r.type}
                  loading="lazy"
                  className="room-img h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-[color:var(--ink)]/10" />
                <span className="absolute top-4 left-4 font-mono-alt text-[color:var(--paper)] bg-[color:var(--ink)]/55 backdrop-blur px-3 py-1.5 rounded-full">
                  Room · 0{i + 1}
                </span>
              </div>

              <div className="p-6 flex flex-col gap-4">
                {/* Room name removed per feedback — only type + description */}
                <h3 className="font-display text-[1.6rem] sm:text-[1.75rem] leading-tight tracking-tight">
                  {r.type}
                </h3>
                <p className="text-[0.95rem] text-[color:var(--ink)]/70 leading-relaxed">
                  {r.blurb}
                </p>

                <ul className="mt-1 grid grid-cols-2 gap-y-2 gap-x-3 text-[0.82rem]">
                  {r.features.map((f) => {
                    const Icon = featureIcon(f);
                    return (
                      <li
                        key={f}
                        className="flex items-center gap-2 text-[color:var(--ink)]/80"
                      >
                        <Icon size={14} className="text-[color:var(--moss)]" />
                        {f}
                      </li>
                    );
                  })}
                </ul>

                <a
                  data-testid={`room-book-${r.id}`}
                  href={WA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center justify-between gap-2 rounded-full border hairline px-5 py-3 font-medium text-[0.9rem] transition-all hover:bg-[color:var(--forest)] hover:text-[color:var(--paper)] hover:border-[color:var(--forest)]"
                >
                  Enquire on WhatsApp <ArrowUpRight size={16} />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
